import { memo, useCallback, type ElementType, type FC, type ReactNode } from "react";
import { ChevronDown, Copy, ExternalLink, Phone, Plus, X } from "lucide-react";
import { WhatsAppOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import CampaignFormField from "../../../shared/CampaignFormField";
import {
  CALL_ACTIVE_DAYS,
  COUNTRY_CODES,
  TEMPLATE_FORM_LIMITS,
  WHATSAPP_EDITOR_KEY,
} from "../WhatsAppCampaignConstants";
import type { WhatsAppButtonDraft, WhatsAppButtonType } from "../WhatsAppCampaignTypes";
import { getAvailableButtonTypes, isButtonType } from "../WhatsAppTemplateFormUtils";

const BUTTON_ICONS: Record<WhatsAppButtonType, ElementType> = {
  visit_website: ExternalLink,
  call_whatsapp: WhatsAppOutlined,
  call_phone: Phone,
  copy_code: Copy,
};

const BUTTONS_KEY = `${WHATSAPP_EDITOR_KEY}.form.buttons`;
const POPUP_CLASS = "campaign-flow__popup";
const SELECT_CLASS = "campaign-flow__select";

interface TemplateButtonsEditorProps {
  buttons: WhatsAppButtonDraft[];
  errors: Record<string, string>;
  showErrors: boolean;
  onAdd: (_type: WhatsAppButtonType) => void;
  onUpdate: (_id: string, _patch: Partial<WhatsAppButtonDraft>) => void;
  onChangeType: (_id: string, _type: WhatsAppButtonType) => void;
  onRemove: (_id: string) => void;
}

const TemplateButtonsEditor: FC<TemplateButtonsEditorProps> = ({
  buttons,
  errors,
  showErrors,
  onAdd,
  onUpdate,
  onChangeType,
  onRemove,
}) => {
  const { t } = useTranslation();
  const availableTypes = getAvailableButtonTypes(buttons);
  const canAdd = buttons.length < TEMPLATE_FORM_LIMITS.maxButtons && availableTypes.length > 0;

  const typeLabel = useCallback((type: WhatsAppButtonType) => t(`${BUTTONS_KEY}.types.${type}`), [t]);

  const renderTypeOption = (option: { value?: string | number | null; label?: ReactNode }) => {
    const Icon = isButtonType(option.value) ? BUTTON_ICONS[option.value] : null;
    return (
      <span className="wa-form-button__option">
        {Icon && <Icon size={14} />}
        {option.label}
      </span>
    );
  };

  const renderButton = (button: WhatsAppButtonDraft) => {
    const { id, type } = button;
    const errorKey = showErrors ? errors[id] : undefined;
    const fieldError = (field: string) =>
      errorKey?.endsWith(`.${field}`) ? t(errorKey) : undefined;
    const typeOptions = getAvailableButtonTypes(buttons, type).map(option => ({
      value: option,
      label: typeLabel(option),
    }));
    const patch = (value: Partial<WhatsAppButtonDraft>) => onUpdate(id, value);

    return (
      <div key={id} className="wa-form-button">
        <div className="wa-form-button__header">
          <span className="wa-form-button__title">{typeLabel(type)}</span>
          <button
            type="button"
            className="wa-form-button__remove"
            onClick={() => onRemove(id)}
            aria-label={t(`${BUTTONS_KEY}.remove`, { name: typeLabel(type) })}
          >
            <X size={14} aria-hidden />
          </button>
        </div>

        <div className="wa-form-button__fields">
          <CampaignFormField label={t(`${BUTTONS_KEY}.typeOfAction`)} htmlFor={`${id}-type`}>
            <StyledDropdown
              id={`${id}-type`}
              value={type}
              options={typeOptions}
              optionRender={renderTypeOption}
              onChange={(value: WhatsAppButtonType) => onChangeType(id, value)}
              className={SELECT_CLASS}
              popupClassName={POPUP_CLASS}
            />
          </CampaignFormField>

          <CampaignFormField
            label={t(`${BUTTONS_KEY}.buttonText`)}
            htmlFor={`${id}-text`}
            hasError={Boolean(fieldError("buttonText"))}
            errorMessage={fieldError("buttonText")}
            helperText={type === "copy_code" ? t(`${BUTTONS_KEY}.fixedByMeta`) : undefined}
          >
            <StyledTextInput
              id={`${id}-text`}
              value={button.text}
              maxLength={TEMPLATE_FORM_LIMITS.buttonText}
              disabled={type === "copy_code"}
              onChange={event => patch({ text: event.target.value })}
              className="campaign-flow__input"
            />
          </CampaignFormField>

          {type === "visit_website" && (
            <>
              <CampaignFormField label={t(`${BUTTONS_KEY}.urlType`)} htmlFor={`${id}-url-type`}>
                <StyledDropdown
                  id={`${id}-url-type`}
                  value={button.urlType}
                  options={[
                    { value: "static", label: t(`${BUTTONS_KEY}.urlTypes.static`) },
                    { value: "dynamic", label: t(`${BUTTONS_KEY}.urlTypes.dynamic`) },
                  ]}
                  onChange={(value: WhatsAppButtonDraft["urlType"]) => patch({ urlType: value })}
                  className={SELECT_CLASS}
                  popupClassName={POPUP_CLASS}
                />
              </CampaignFormField>
              <CampaignFormField
                label={t(`${BUTTONS_KEY}.websiteUrl`)}
                htmlFor={`${id}-url`}
                hasError={Boolean(fieldError("buttonUrl"))}
                errorMessage={fieldError("buttonUrl")}
              >
                <StyledTextInput
                  id={`${id}-url`}
                  value={button.url}
                  type="url"
                  placeholder="https://"
                  onChange={event => patch({ url: event.target.value })}
                  className="campaign-flow__input"
                />
              </CampaignFormField>
            </>
          )}

          {type === "copy_code" && (
            <CampaignFormField
              label={t(`${BUTTONS_KEY}.offerCode`)}
              htmlFor={`${id}-code`}
              hasError={Boolean(fieldError("offerCode"))}
              errorMessage={fieldError("offerCode")}
            >
              <StyledTextInput
                id={`${id}-code`}
                value={button.offerCode}
                maxLength={TEMPLATE_FORM_LIMITS.offerCode}
                placeholder={t(`${BUTTONS_KEY}.offerCodePlaceholder`)}
                onChange={event => patch({ offerCode: event.target.value })}
                className="campaign-flow__input"
              />
            </CampaignFormField>
          )}

          {type === "call_whatsapp" && (
            <CampaignFormField label={t(`${BUTTONS_KEY}.activeFor`)} htmlFor={`${id}-days`}>
              <StyledDropdown
                id={`${id}-days`}
                value={button.activeDays}
                options={CALL_ACTIVE_DAYS.map(days => ({
                  value: days,
                  label: t(`${BUTTONS_KEY}.days`, { count: days }),
                }))}
                onChange={(value: number) => patch({ activeDays: value })}
                className={SELECT_CLASS}
                popupClassName={POPUP_CLASS}
              />
            </CampaignFormField>
          )}

          {type === "call_phone" && (
            <>
              <CampaignFormField label={t(`${BUTTONS_KEY}.country`)} htmlFor={`${id}-country`}>
                <StyledDropdown
                  id={`${id}-country`}
                  value={button.countryCode}
                  options={COUNTRY_CODES.map(code => ({ value: code, label: code }))}
                  onChange={(value: string) => patch({ countryCode: value })}
                  className={SELECT_CLASS}
                  popupClassName={POPUP_CLASS}
                />
              </CampaignFormField>
              <CampaignFormField
                label={t(`${BUTTONS_KEY}.phoneNumber`)}
                htmlFor={`${id}-phone`}
                hasError={Boolean(fieldError("phoneNumber"))}
                errorMessage={fieldError("phoneNumber")}
              >
                <StyledTextInput
                  id={`${id}-phone`}
                  value={button.phoneNumber}
                  inputMode="tel"
                  placeholder={t(`${BUTTONS_KEY}.phonePlaceholder`)}
                  onChange={event => patch({ phoneNumber: event.target.value })}
                  className="campaign-flow__input"
                />
              </CampaignFormField>
            </>
          )}
        </div>

        {type === "visit_website" && (
          <label className="wa-form-button__checkbox">
            <input
              type="checkbox"
              checked={button.trackConversation}
              onChange={event => patch({ trackConversation: event.target.checked })}
            />
            {t(`${BUTTONS_KEY}.trackConversation`)}
          </label>
        )}
        {type === "call_whatsapp" && (
          <p className="campaign-flow-field__helper">{t(`${BUTTONS_KEY}.callWhatsappHint`)}</p>
        )}
      </div>
    );
  };

  return (
    <section className="wa-form-section">
      <header className="wa-form-section__header">
        <div className="wa-form-section__heading">
          <h3 className="wa-form-section__title">
            {t(`${BUTTONS_KEY}.title`)}
            <span className="wa-form-section__optional">{t(`${BUTTONS_KEY}.optional`)}</span>
          </h3>
          <p className="wa-form-section__subtitle">{t(`${BUTTONS_KEY}.subtitle`)}</p>
        </div>
        <StyledDropdown
          dropdownType="menu"
          items={availableTypes.map(type => {
            const Icon = BUTTON_ICONS[type];
            return { key: type, icon: <Icon size={14} />, label: typeLabel(type) };
          })}
          onSelectMenu={key => isButtonType(key) && onAdd(key)}
        >
          <button
            type="button"
            className="button button--outline wa-form-section__add"
            disabled={!canAdd}
          >
            <Plus size={14} aria-hidden />
            {t(`${BUTTONS_KEY}.add`)}
            <ChevronDown size={14} aria-hidden />
          </button>
        </StyledDropdown>
      </header>

      {buttons.length > 0 && (
        <div className="wa-form-buttons">
          <span className="wa-form-buttons__label">{t(`${BUTTONS_KEY}.callToAction`)}</span>
          {buttons.map(renderButton)}
        </div>
      )}
    </section>
  );
};

export default memo(TemplateButtonsEditor);
